import axios from 'axios';
import { apiClient } from './client';
import { registerSessionCache } from '../lib/session-caches';
import { narrowAttachments } from '../lib/attachments';
import type { MessageAttachment } from '../types/api';

/**
 * Calendar API client (P3-CAL-FE) — backend `apps/chat-server/src/modules/calendar/calendar.routes.ts` 와 1:1.
 *
 * 후속: yaml SSOT 정합 (현재 manual type — 다음 사이클에 generated alias 로 좁히기 권장).
 */

// 캘린더 상세화(2026-06-07) — todo(할일)·anniversary(기념일)·vacation(휴가) additive 추가.
export type CalendarEventType = 'meeting' | 'personal' | 'external' | 'training' | 'todo' | 'anniversary' | 'vacation';

/**
 * 일정에 붙은 예약 한 건 — 배지에 필요한 «최소치»다. 상세 정보는 예약 화면이 갖는다.
 *
 * ⭐ 서버의 `EventReservationView`(chat-server `calendar-helpers.ts`)와 «칸 이름까지» 같다 —
 *   2026-09-30 에 그 다섯 칸으로 서는 것을 확인하고 이 꼴로 맞췄다.
 * ⚠ `resourceName` 을 «서버가 준다» — 배지가 자원 목록을 따로 부르지 않게 하려는 것이다.
 */
export interface EventReservationDto {
  id: string;
  /**
   * 'pending' | 'approved' — ⭐ 서버가 «취소·반려는 빼고» 준다
   * (`EVENT_PUBLIC_SELECT.reservations.where = status in ['pending','approved']`).
   * 타입은 넓게 두어 값이 늘어도 안 깨진다.
   */
  status: string;
  resourceId: string;
  resourceName: string;
  /** 반복 일정의 회차. 단일 일정이면 null. */
  occurrenceAt: string | null;
}

export interface CalendarEventDto {
  id: string;
  creatorId: string;
  title: string;
  description: string | null;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  type: string;
  /** 할일(type='todo') 완료 상태(additive). 그 외 type 은 항상 false. */
  done?: boolean;
  color: string | null;
  location: string | null;
  /**
   * ⚠ `name` 은 **서버가 아직 안 보낸다** — 옵셔널로만 선언해 둔다.
   *   · 까닭 — 일간 표(2026-09-09 결정)가 참석자 이름을 그리는데, 지금 응답에는 userId 뿐이다.
   *     같은 라우트가 작성자에게는 이미 이름을 붙인다(`creator.name`) ⇒ 같은 꼴이 오면 된다.
   *   · 만료 조건 — 서버가 `attendees[].name` 을 보내기 시작하면.
   *   · 그때 할 일 — 일간 표의 참석자 열이 저절로 찬다(코드는 이미 그 칸을 읽는다).
   *     그리고 `EventDetailModal` 의 조직도 우회(`lib/attendeeNames.ts`)를 걷어낸다.
   *   ⭐ 2026-10-13 — 만료 조건이 «발동했다»(목록·상세·POST·PATCH 응답 모두 — EVENT_PUBLIC_SELECT).
   *     ⏸ `EventDetailModal` 의 우회 걷어내기는 그 창을 고치는 판의 몫으로 남긴다(이 판은 손대지 않았다).
   */
  // ⭐ 2026-10-13 — 서버가 목록·상세 응답에 `name`·`avatarUrl` 을 싣기 시작했다(옛 가지 da074f9bd 이식).
  //   ⚠ 관계가 빈 참석자(탈퇴 등)는 `name: null` 이다 — 읽는 곳이 걸러 낸다. 옛 응답·mock 은 칸이 없다.
  attendees: Array<{ userId: string; status: string; respondedAt?: string | null; name?: string | null; avatarUrl?: string | null }>;
  /**
   * ⭐ 2026-10-15 — 반복 회차의 참석자를 「이 일정만」으로 따로 정해 두었나(그때 `attendees` 는 그 회차의 명단이다).
   *   옛 응답·단일 일정은 칸이 없다.
   */
  attendeesOverridden?: boolean;
  /**
   * ⚠ 팀 캘린더 일정의 참여자 — **서버가 아직 안 보낸다**(캘린더 모듈에 0건).
   *   옛 가지는 `listEvents` 에 `teamMembers=1` 질의를 붙여 받는다.
   *   · 만료 조건 — 서버가 그 칸을 보내고 프론트가 그 질의를 붙이면.
   *   · 그때 할 일 — 일간 표의 참석자 열과 `isEventAssignedToMe` 가 저절로 정확해진다.
   */
  teamMembers?: Array<{ userId: string; name: string }> | null;
  /**
   * ⚠ 내가 건 알림 — **서버가 목록 응답에 아직 안 붙였다**(상세 라우트에만 있다).
   *   · 까닭 — 일간 표가 시간 셀 아래에 알림 칩을 그린다(시안 B · 2026-09-09).
   *   · 만료 조건 — 목록 응답에 `reminders` 가 실리면.
   *   · 그때 할 일 — `DayAgendaView` 의 `renderReminders` 가 저절로 그린다(지금은 늘 빈 배열).
   *     그리고 a3(「상세에서 알림을 바꾸면 일간 표가 따라온다」)를 그 위에 얹는다.
   *   ⭐ 2026-10-13 — 만료 조건이 «발동했다»(chat-server `calendar-list-reminders.ts` · 요청자 본인 것만).
   */
  reminders?: ReminderDto[] | null;
  /**
   * 5-tier OR 가드의 (5) calendar 분기용 첨부 — backend calendar.routes.ts compose 가
   * creator ownership + verified + storagePath 강제. yaml MessageAttachment[] shape.
   * pattern-frontend-backend-wire-contract-drift §"cross-domain 정착 검증" N=3 적용.
   */
  attachments: MessageAttachment[];
  createdAt: string;
  // Phase 1 (T-cal19) — 백엔드 EventPublic 이 이미 반환하던 필드 노출(옵셔널, 회귀 안전).
  // recurrenceRule: master RRULE(null=단일). occurrenceAt: expand 된 occurrence 시작(null=단일/master).
  // isException: occurrence override 적용본 여부. calendarId: 소속 캘린더(null=기본 폴백).
  recurrenceRule?: string | null;
  occurrenceAt?: string | null;
  isException?: boolean | null;
  calendarId?: string | null;
  /**
   * 이 일정에 붙은 자원 예약 — 회의실 승인 배지가 읽는다.
   * ⚠ 옛 응답·mock 은 이 칸이 «없을 수» 있어 옵셔널이다(그래서 배지가 `?? []` 로 읽는다).
   * ⭐ 회차 응답은 «그 회차의 것만», master·단일 응답은 «전부» 담는다(서버 쪽 규칙).
   */
  reservations?: EventReservationDto[];
  /** 작성자(누가 등록했는지). 구버전 응답/mock 은 없을 수 있어 옵셔널. */
  creator?: { id: string; name: string; avatarUrl: string | null } | null;
}

interface CalendarEventDtoRaw extends Omit<CalendarEventDto, 'attachments'> {
  attachments: unknown;
}

export interface ListEventsResponse {
  events: CalendarEventDto[];
  from: string;
  to: string;
}

export interface RecurrenceInput {
  freq: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  interval?: number;
  until?: string;
  count?: number;
  byday?: Array<'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU'>;
  /**
   * ⭐ 2026-10-13 이식 — 「몇째 주 무슨 요일」(1~5, 5 = 마지막 주) · 매년의 달 · 날짜 방식의 날 ·
   *   그리고 시작의 «현지 날짜». 서버가 이 날짜로 규칙을 UTC 기준으로 옮겨 적는다
   *   (chat-server `localizedRuleTokens` — KST 새벽 일정이 하루 밀리지 않게).
   */
  bysetpos?: number;
  bymonth?: number;
  bymonthday?: number;
  localStartDate?: string;
  /**
   * ⭐ 2026-10-14 (E-3 보강) — 종료 「기간」의 N(단위는 freq). 서버가 «새로 바뀌기 시작하는 회차»부터 다시 세어
   *   until 을 정한다(「이후」= 고른 회차, 「전체」= 오늘 이후 첫 회차). until 은 옛 서버를 위해 함께 보낸다.
   */
  period?: number;
}

export interface CreateEventInput {
  title: string;
  description?: string;
  startAt: string;
  endAt: string;
  isAllDay?: boolean;
  type?: CalendarEventType;
  done?: boolean;
  color?: string;
  location?: string;
  calendarId?: string;
  recurrence?: RecurrenceInput;
  attendeeUserIds?: string[];
  /**
   * 첨부 — 빈 배열 또는 undefined 시 backend 가 null 처리. yaml SSOT MessageAttachment[]
   * 와 정합. backend M-A2 동형 ownership 검증 통과 시 calendar_events.attachments JSONB
   * 에 결합 + 5-tier OR 가드의 (5) 분기에 사용 가능.
   */
  attachments?: MessageAttachment[];
  /**
   * ⭐ 2026-11-02 (지시 57) — 외부 참석자(주소록 연락처). 최대 50명. 이메일은 서버가 연락처에서 읽는다.
   *   저장하면 서버가 초대 메일을 «자동으로» 보낸다(체크 칸 없음). 없으면 키를 안 보낸다.
   */
  externalAttendees?: ExternalAttendeeNewInput[];
}

export interface PatchEventInput {
  title?: string;
  description?: string;
  startAt?: string;
  endAt?: string;
  isAllDay?: boolean;
  type?: CalendarEventType;
  done?: boolean;
  color?: string;
  location?: string;
  calendarId?: string;
  /**
   * ⭐ 2026-10-15 — 참석자 명단(바꿨을 때만 «전체 명단»을 싣는다). 아무도 없으면 만든 사람이 참석자다(만들 때와 같은 규칙).
   *   반복 일정은 날짜·시간과 같은 범위 규칙을 따른다 — 「이 일정만」은 지난 회차도 가능, 지난 회차에서 연 「이후」·「전체」는 오늘 이후부터.
   */
  attendeeUserIds?: string[];
  /**
   * ⭐ 2026-10-13 — 반복 설정 변경. 이미 반복인 일정은 `scope=future` 로만(서버 400 RECURRENCE_SCOPE_INVALID),
   *   반복이 아닌 일정은 그대로(scope=all) 규칙이 붙는다.
   * ⭐ 2026-10-14 (반복 규칙 F) — 이미 반복인 일정도 scope=all 로 바꿀 수 있다: 지난 회차는 그대로, 오늘 이후 첫 회차부터.
   *   scope=this 만 400 RECURRENCE_SCOPE_INVALID.
   */
  recurrence?: RecurrenceInput;
  /**
   * ⭐ 2026-10-14 (반복 규칙 G) — 「전체 일정」 내용 수정이 '이 일정만'으로 고친 칸과 겹칠 때의 결정.
   *   서버가 409 RECURRENCE_ALL_OVERRIDE_CONFLICT 를 줄 때만 싣는다(recurrence-override-conflict.ts).
   */
  overrideConflict?: 'overwrite' | 'keep';
  /**
   * ⭐ 2026-10-14 (반복 규칙 G 보강) — 날짜·시간·반복 변경으로 '이 일정만' 수정 내역이 사라져도 진행.
   *   서버가 409 RECURRENCE_OVERRIDES_WILL_BE_LOST 를 줄 때 «예»를 고르면 싣는다.
   */
  confirmOverrideLoss?: boolean;
  /**
   * ⭐ 2026-11-02 (지시 57) — 외부 참석자 «전체 명단». ⛔ 바꾸지 않았으면 키를 «보내지 않는다»(서버가 그대로 둔다).
   *   `[]` 는 «모두 빼기»다(보낸 적 있는 사람에게 취소 메일). 「이 일정만」(scope=this)과 함께 보내면 서버가 400 이다.
   * ⭐ 49D — 이미 있는 사람은 늘 `{ id }`, 새로 고른 사람만 `{ contactId }`. contactId 만 보내면 연락처가 지워진 사람이 빠진다.
   */
  externalAttendees?: ExternalAttendeeInput[];
}

/* ═══════════════ 외부 참석자 + 자동 초대 메일 (2026-11-02 · 지시 57 · 서버 49B 계약) ═══════════════
 *
 * 칸 이름이 바뀔 수 있어 모양은 «여기 한 곳»에 모은다. 서버가 이 기능을 모르는 판이면 응답에
 * `externalInvite` 가 «아예 없다» — 그때 화면은 주소록 고르기를 숨기고 종전처럼 동작한다.
 */
/** 새로 고른 사람 — 연락처 id. 새 일정(POST)에는 이것만 쓴다. */
export interface ExternalAttendeeNewInput {
  contactId: string;
}
/**
 * ⭐ 49D — PATCH 의 한 항목. 이미 있는 외부 참석자는 `{ id }`(응답의 externalAttendees[].id)로 «그대로 둔다» —
 *   연락처가 지워져 contactId 가 null 인 사람도 그래야 빠지지 않는다. 둘을 함께 주면 400.
 */
export type ExternalAttendeeInput = ExternalAttendeeNewInput | { id: string };

/** 마지막 메일의 상태 — `queued` 는 저장이 커밋된 뒤 배경에서 `sent`·`failed` 로 바뀐다. */
export type ExternalInviteStatus =
  | 'queued' | 'sent' | 'failed'
  | 'skipped_no_account' | 'skipped_daily_limit' | 'skipped_not_organizer';

export interface ExternalLastInvite {
  method: 'REQUEST' | 'CANCEL';
  kind: 'invite' | 'update' | 'cancel';
  status: ExternalInviteStatus | string;
  occurrenceAt: string | null;
  at: string | null;
}

export interface ExternalAttendeeDto {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  contactId: string | null;
  lastInvite: ExternalLastInvite | null;
}

/** `available:false` 의 까닭. 화면 문구는 웹이 정한다(코드를 그대로 보이지 않는다). */
export type ExternalInviteReason = 'NO_SENDING_ACCOUNT' | 'NOT_ORGANIZER';

/** 이번 저장·삭제·다시 보내기로 나갈(또는 못 나간) 메일 한 통. */
export interface ExternalInviteResult {
  attendeeId: string | null;
  email: string;
  name: string | null;
  method: 'REQUEST' | 'CANCEL';
  kind: 'invite' | 'update' | 'cancel';
  occurrenceAt: string | null;
  status: ExternalInviteStatus | string;
  reason: string | null;
}

export interface ExternalInviteInfo {
  available: boolean;
  reason?: ExternalInviteReason | string | null;
  /** 저장·삭제·다시 보내기 응답에만 있다(GET 에는 없다). */
  results?: ExternalInviteResult[];
  /** 일정은 저장됐는데 외부 참석자 처리가 실패한 드문 경우. */
  error?: string;
}

/** 응답에 덧붙는 두 칸. 둘 다 없으면 «변화 없음»(또는 옛 서버)이다. */
export interface ExternalInviteEnvelope {
  externalAttendees?: ExternalAttendeeDto[];
  externalInvite?: ExternalInviteInfo;
}

/** 저장 함수들이 응답의 외부 참석자 칸을 넘겨주는 손잡이(선택) — 안 주면 종전과 같다. */
export type ExternalInviteSink = (env: ExternalInviteEnvelope) => void;

/** 응답 본문에서 두 칸만 안전하게 꺼낸다 — 모양이 틀리면 그 칸은 없는 것으로 본다. */
export function readExternalEnvelope(data: unknown): ExternalInviteEnvelope {
  if (!data || typeof data !== 'object') return {};
  const d = data as { externalAttendees?: unknown; externalInvite?: unknown };
  const out: ExternalInviteEnvelope = {};
  if (Array.isArray(d.externalAttendees)) out.externalAttendees = d.externalAttendees as ExternalAttendeeDto[];
  if (d.externalInvite && typeof d.externalInvite === 'object' && typeof (d.externalInvite as { available?: unknown }).available === 'boolean') {
    out.externalInvite = d.externalInvite as ExternalInviteInfo;
  }
  return out;
}

export interface EventDetailResponse extends ExternalInviteEnvelope {
  event: CalendarEventDto;
}

/** 일정 상세 — `GET /calendar/events/:id`. 새 서버는 외부 참석자와 «지금 보낼 수 있나»를 덧붙인다. */
export async function getEventDetail(id: string, signal?: AbortSignal): Promise<EventDetailResponse> {
  const res = await apiClient.get<{ event: CalendarEventDtoRaw }>(`/calendar/events/${id}`, { signal });
  const event = res.data.event;
  return { ...readExternalEnvelope(res.data), event: { ...event, attachments: narrowAttachments(event.attachments) } };
}

/**
 * 외부 참석자에게 초대 메일 다시 보내기(작성자만). 같은 일정은 1분에 한 번(409).
 * ⭐ 57B — 늘 «보낼 사람»을 골라 싣는다. 서버는 비우면 남은 전원(이미 받은 사람 포함)에게 다시 보내므로
 *   빈 목록으로는 부르지 않는다 — 부르면 여기서 막는다.
 */
export async function resendExternalInvites(id: string, attendeeIds: string[]): Promise<ExternalInviteEnvelope> {
  if (attendeeIds.length === 0) throw new Error('resendExternalInvites: no attendee');
  const res = await apiClient.post(`/calendar/events/${id}/external-invites`, { attendeeIds });
  return readExternalEnvelope(res.data);
}

/**
 * 이 서버가 외부 참석자를 아나 — 새 일정 창처럼 «응답을 받기 전»에 알아야 할 때 한 번 묻는다.
 * 없는 일정(0)에 다시 보내기를 부른다: 새 서버는 «일정 없음»(404 + 코드)을, 옛 서버는 «길 없음»(404, 코드 없음)을 준다.
 * 일정 0 은 없으므로 아무것도 보내거나 바꾸지 않는다. 판정할 수 없으면 null(모름).
 */
export async function probeExternalInviteRoute(): Promise<boolean | null> {
  try {
    await apiClient.post('/calendar/events/0/external-invites', {});
    return true;
  } catch (err) {
    if (!axios.isAxiosError(err) || !err.response) return null;
    const { status, data } = err.response;
    const code = data && typeof data === 'object' ? (data as { code?: unknown }).code : undefined;
    if (status === 404) return code === 'EVENT_NOT_FOUND';
    if (status === 400 || status === 409) return true;
    return null;
  }
}

interface ListEventsResponseRaw {
  events: CalendarEventDtoRaw[];
  from: string;
  to: string;
}

export async function listEvents(
  from: string,
  to: string,
  calendarIds?: string[],
  /**
   * 요청 취소 — 검색이 쓴다(2026-09-28, additive · 기존 3-인자 호출 무영향).
   * 타자를 칠 때마다 250ms 디바운스로 다시 부르는데, 앞 요청을 안 끊으면 늦게 온 응답이
   * 새 결과를 덮을 수 있다. `EventSearch` 는 반영을 막는 플래그도 함께 쓰지만, 그것은
   * «그리지 않는» 것이고 이쪽은 «보내지 않는» 것이다.
   */
  signal?: AbortSignal,
  /**
   * 「팀원 일정도 보기」(2026-10-13, additive · 기존 2~4-인자 호출 무영향).
   * 켜면 `teamMembers=1` 을 붙인다 — 서버가 내 팀(조직도팀·프로젝트팀)의 «다른» 구성원 일정을 함께 준다.
   * ⚠ 꺼져 있으면 «아무것도 안 붙인다» — 종전 요청과 글자 그대로 같다(`teamMembers=0` 도 안 보낸다).
   */
  options?: { teamMembers?: boolean },
): Promise<ListEventsResponse> {
  // T-cal18 — calendarIds 옵셔널(기존 2-인자 호출 무영향). 지정 시 쉼표구분으로 표시 캘린더 필터.
  const params: Record<string, string> = { from, to };
  if (calendarIds && calendarIds.length > 0) params.calendarIds = calendarIds.join(',');
  if (options?.teamMembers) params.teamMembers = '1';
  const res = await apiClient.get<ListEventsResponseRaw>('/calendar/events', { params, signal });
  // 방어 가드(additive) — 2xx 인데 events 가 배열이 아닌(형태불일치·비-JSON) 응답에도 map 크래시 차단.
  const rawEvents = Array.isArray(res.data?.events) ? res.data.events : [];
  return {
    ...res.data,
    events: rawEvents.map((e) => ({ ...e, attachments: narrowAttachments(e.attachments) })),
  };
}

export async function createEvent(input: CreateEventInput, sink?: ExternalInviteSink): Promise<CalendarEventDto> {
  const res = await apiClient.post<{ event: CalendarEventDtoRaw | null }>('/calendar/events', input);
  sink?.(readExternalEnvelope(res.data));
  const event = res.data.event;
  if (!event) {
    throw new Error('createEvent: backend returned null event (race or recreated)');
  }
  return { ...event, attachments: narrowAttachments(event.attachments) };
}

export async function patchEvent(id: string, input: PatchEventInput, sink?: ExternalInviteSink): Promise<CalendarEventDto> {
  const res = await apiClient.patch<{ event: CalendarEventDtoRaw | null }>(`/calendar/events/${id}`, input);
  sink?.(readExternalEnvelope(res.data));
  const event = res.data.event;
  if (!event) {
    throw new Error('patchEvent: backend returned null event (race or recreated)');
  }
  return { ...event, attachments: narrowAttachments(event.attachments) };
}

/**
 * 일정 삭제. `opts` 를 안 주면 종전 그대로 — `scopeParams` 가 undefined 를 주므로
 * 요청이 바이트 단위로 같다(회귀 0). 반복 일정의 「이 회차만·이후 모두」는 opts 로 간다.
 * (EventScopeOpts·scopeParams 선언은 이 파일 아래쪽 EventScope 절에 있다.)
 *
 * ## ⚠ 왜 `MoveEventResult` 같은 반환이 «없나» — 비대칭의 까닭
 *
 * 서버는 삭제 응답에 플래그를 싣는다(`deletedWholeSeries` · `deletedExceptions`). 그런데
 * **이 함수는 응답을 읽지 않는다**(`Promise<void>`) ⇒ 그것을 둘 «자리»가 없다.
 * ⇒ ★ 그래서 이동 쪽(`patchEventScoped` → `MoveEventResult.movedWholeSeries`)과 **비대칭이다.**
 *   ⛔ 그 비대칭은 «빠뜨린 것이 아니라» 두 함수의 계약이 다른 것이다 —
 *   맞추려면 **아무도 안 읽는 반환 타입을 새로 지어야** 한다.
 * ⇒ 바꿀 조건: 호출부가 그 플래그를 **실제로 읽어야** 할 때
 *   (예: 「전부 지웠습니다」를 사용자에게 말해야 할 때). 그때 반환을 열고 타입을 세운다.
 *   재는 법: `grep -rn "deletedWholeSeries" apps/frontend/src` ⇒ 지금 **0**.
 */
export async function deleteEvent(id: string, opts?: EventScopeOpts, sink?: ExternalInviteSink): Promise<void> {
  const params = scopeParams(opts);
  const res = await apiClient.delete(`/calendar/events/${id}`, params ? { params } : undefined);
  sink?.(readExternalEnvelope(res?.data));
}

// 참석자 RSVP (그룹 G) — 본인 status 변경. backend PATCH /calendar/events/:id/rsvp.
export type RsvpStatus = 'ACCEPTED' | 'DECLINED' | 'TENTATIVE';

/**
 * ⭐ 2026-10-15 — 반복 일정은 «회차별»로 응답한다: `occurrenceAt` 을 주면 그 회차에만 답한다.
 *   안 주면 종전 그대로(일정 전체 — 단일 일정·옛 호출자).
 */
export async function setRsvp(eventId: string, status: RsvpStatus, occurrenceAt?: string | null): Promise<void> {
  if (occurrenceAt) {
    await apiClient.patch(`/calendar/events/${eventId}/rsvp`, { status }, { params: { occurrenceAt } });
    return;
  }
  await apiClient.patch(`/calendar/events/${eventId}/rsvp`, { status });
}

/** 시안 line 1316~1322 의 색상 매핑 — backend type enum 정합 (4종).
 *  시안의 vacation/business 는 backend 미지원 — personal/training 으로 흡수.
 *  fallback hex 제거 (tokens.css 에 --accent-* 6종 정의 완료). */
// 캘린더 테마(2026-06-07 T1) — 카테고리 색을 `--cal-type-*` 토큰화. fallback 이 기존 accent 라
// 테마 미주입 시 현행 동일(회귀 0). 테마는 .calendar-page/대시보드 루트에 --cal-type-* inline 주입으로 덮어씀.
export const TYPE_COLOR_MAP: Record<CalendarEventType, { label: string; color: string }> = {
  meeting:  { label: '회의',  color: 'var(--cal-type-meeting, var(--accent-blue))' },
  personal: { label: '개인',  color: 'var(--cal-type-personal, var(--accent-green))' },
  external: { label: '외부',  color: 'var(--cal-type-external, var(--accent-pink))' },
  training: { label: '교육',  color: 'var(--cal-type-training, var(--accent-purple))' },
  todo:        { label: '할일',   color: 'var(--cal-type-todo, var(--accent-orange))' },
  anniversary: { label: '기념일', color: 'var(--cal-type-anniversary, var(--accent-red))' },
  vacation:    { label: '휴가',   color: 'var(--cal-type-vacation, var(--accent-teal))' },
};

export const EVENT_TYPES: CalendarEventType[] = ['meeting', 'personal', 'external', 'training', 'todo', 'anniversary', 'vacation'];

// ───────────────────────────────────────────────────────────────────────
// Phase 1 — 다중 캘린더 / 알림 / 공휴일 (T-cal18). 기존 함수/타입 무수정, 추가만.
// ───────────────────────────────────────────────────────────────────────

export interface CalendarDto {
  id: string;
  ownerId: string;
  /** team-calendar — null = 개인 캘린더, 값 = 그 팀의 공유 캘린더. */
  teamId: string | null;
  /** 팀 캘린더면 팀 이름(사이드바 그룹 헤더). 개인이면 null. */
  teamName: string | null;
  /**
   * org-team-calendar (migration 229 · 2026-10-13) — 값 = 그 조직도팀(부서)의 공유 캘린더.
   * 옵셔널 — 옛 서버·시험 흉내는 이 칸을 안 보낸다(falsy 로 읽는다).
   * 팀원 목록이 필요하면 `api/organization.ts` 의 `getTeam(orgTeamId)` 가 준다(GET /organization/teams/:id).
   */
  orgTeamId?: string | null;
  /** 조직도팀 캘린더면 팀 이름(사이드바 그룹 헤더). 아니면 null. */
  orgTeamName?: string | null;
  name: string;
  color: string;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCalendarInput {
  name: string;
  color: string;
  sortOrder?: number;
  /** team-calendar — 지정 시 그 팀의 공유 캘린더(내가 멤버여야 함). 미지정 = 개인 캘린더. */
  teamId?: string;
  /** org-team-calendar (migration 229) — 지정 시 그 조직도팀의 공유 캘린더. teamId 와 동시 불가(서버 400). */
  orgTeamId?: string;
}
export interface PatchCalendarInput {
  name?: string;
  color?: string;
  sortOrder?: number;
}

export async function listCalendars(): Promise<CalendarDto[]> {
  const res = await apiClient.get<{ calendars: CalendarDto[] }>('/calendars');
  return Array.isArray(res.data?.calendars) ? res.data.calendars : [];
}

export async function createCalendar(input: CreateCalendarInput): Promise<CalendarDto> {
  const res = await apiClient.post<{ calendar: CalendarDto }>('/calendars', input);
  return res.data.calendar;
}

export async function patchCalendar(id: string, input: PatchCalendarInput): Promise<CalendarDto> {
  const res = await apiClient.patch<{ calendar: CalendarDto }>(`/calendars/${id}`, input);
  return res.data.calendar;
}

export async function deleteCalendar(id: string): Promise<void> {
  await apiClient.delete(`/calendars/${id}`);
}

// ── 알림 (event 하위) ──
export type ReminderMethod = 'popup' | 'email';

/**
 * 반복 일정에서 「어디까지 손대나」 — 이 회차만 / 이후 모두 / 전부.
 *
 * ⭐ 2026-09-29 — 서버가 받는지 «재고» 실어 보내기 시작했다(종전 주석의 숙제):
 *     apps/chat-server/src/modules/calendar/calendar-helpers.ts:222  eventScopeQuerySchema
 *       scope: enum['this','future','all'] · occurrenceAt: date-time
 *     calendar.routes.ts:387 (PATCH) · :739 (DELETE)  둘 다 querystring 으로 물려 있다.
 *   scope 를 안 붙이면 서버가 'all' 로 본다 — 즉 인자를 안 주는 기존 호출은 바이트 단위로 종전과 같다.
 *   this/future 는 서버가 occurrenceAt(그 회차의 시작 시각)을 «필수»로 요구한다(:397 400).
 */
export type EventScope = 'this' | 'future' | 'all';

export interface EventScopeOpts {
  scope?: EventScope;
  /** expand 된 occurrence 의 시작 시각(ISO). scope='this'|'future' 필수. */
  occurrenceAt?: string | null;
}

/**
 * scope 쿼리 조립.
 *
 * ## ⭐ 'all' 에도 `occurrenceAt` 을 싣는다 (2026-09-30) — 서버가 그것을 기다린다
 *
 * 반복 일정의 «회차»를 끌어 옮기고 「전체 일정」을 고르면, 보내는 `startAt` 은 «그 회차»의
 * 새 시각이다. 서버가 그것을 그대로 master 에 쓰면 **DTSTART 가 그 회차로 밀려**
 * 앞 회차가 아예 펼쳐지지 않고, **예외 행이 고아가 되어 「이 회차만 취소」가 되살아난다.**
 *
 * ⇒ 서버는 `occurrenceAt` 을 받으면 **「옮겨진 만큼만」** 시리즈를 민다
 *   (`calendar.routes.ts` 의 `planSeriesShift` · 2026-09-30 머지).
 *   그 주석이 «깨어나는 조건»을 이 함수로 적어 두었다 — 여기가 그 자리다.
 *
 * ⛔ **순서가 중요하다.** 서버가 먼저 들어가야 한다. 클라이언트가 먼저 보내면 서버가
 *   조용히 무시해 「고쳐진 것처럼 보이지만 여전히 밀린다」가 된다.
 *
 * ## ⚠ 「바이트 단위로 같다」는 성질은 지킨다
 *
 * 종전 주석이 「'all' 은 쿼리를 안 붙여 기존 요청과 바이트 단위로 같다」고 적어 두었다.
 * ⇒ 그래서 **`occurrenceAt` 이 있을 때만** 쿼리가 생기고, 그때도 `scope` 는 «안 붙인다» —
 *   서버가 `req.query.scope ?? 'all'` 로 읽으므로 뜻이 같다.
 *   ⇒ 반복이 아닌 일정(`occurrenceAt` 없음)의 요청은 **전과 한 바이트도 다르지 않다.**
 */
function scopeParams(opts?: EventScopeOpts): Record<string, string> | undefined {
  const scope = opts?.scope;
  if (!scope || scope === 'all') {
    // 회차를 안 낀 요청은 종전 그대로 — 쿼리 없음.
    return opts?.occurrenceAt ? { occurrenceAt: opts.occurrenceAt } : undefined;
  }
  const params: Record<string, string> = { scope };
  if (opts?.occurrenceAt) params.occurrenceAt = opts.occurrenceAt;
  return params;
}

/** 이동 결과 — scope 별로 서버 응답 shape 이 달라 호출부가 분기하지 않도록 하나로 normalize 한다. */
export interface MoveEventResult {
  scope: EventScope;
  /**
   * scope='future' 로 뒷부분이 분리되며 새로 생긴 master id. 그 외 null.
   *
   * ⚠ 2026-10-06 — `scope='future'` 라도 **null 일 수 있다.** 서버가 「쪼개면 앞 master 가 빈
   *   껍데기가 된다」고 판단하면 **쪼개지 않고 시리즈를 통째로 옮기고**(사용자 결정 ⓐ) 그때는
   *   새 master 가 없어 이 칸을 **안 보낸다**(사용자 결정 ⓠ) ⇒ 아래 `?? null` 로 null 이 된다.
   *   ⇒ ★ 그 경우를 가르려면 `movedWholeSeries` 를 본다.
   */
  newMasterId: string | null;
  /**
   * ⭐ 서버가 **쪼개지 않고 시리즈를 통째로 옮겼다**(2026-10-06 · 사용자 결정 ⓐ·ⓠ).
   *
   * ⛔ 왜 칸을 더했나: 종전에는 「`scope='future'` 면 새 master 가 생긴다」가 늘 참이었다. 그 길이
   *   갈라졌으므로 **`newMasterId` 가 null 인 까닭이 둘**이 된다(① 이 scope 가 아니다 ② 통째로 옮겼다).
   *   ⇒ 그 둘을 못 가르면 「같은 이름에 다른 뜻」이 된다 — 이 레포가 여러 번 헛짚은 자리다.
   * ⚠ 지금 이 값을 **읽는 곳은 0** 이다(`newMasterId` 도 그렇다 — 설계·관제가 따로 쟀다).
   *   그래도 타입을 서버와 맞춰 둔다: 「서버만 고치고 타입은 거짓으로 남기는」 쪽을 피한다.
   */
  movedWholeSeries: boolean;
}

/**
 * scope 를 붙인 PATCH — 반복 일정의 수정·이동 공용.
 *
 * patchEvent 와 나눈 이유: scope=this 는 `{scope,occurrenceAt,updated}`, scope=future 는
 * `{scope,splitAt,newMasterId}` 를 주고 event 를 주지 않는다. patchEvent 의 반환 계약
 * (CalendarEventDto 보장)을 유지하려면 같은 함수에 담을 수 없다 — 기존 호출부 회귀 차단.
 *
 * 서버가 막는 조합(호출부가 미리 비활성화하는 편이 낫다):
 *   - scope='this'  + calendarId 변경 → 400 CALENDAR_MOVE_SCOPE_INVALID (캘린더는 master 속성)
 *   - scope='future' + startAt < occurrenceAt → 400 (자른 앞 시리즈와 겹침)
 *   - scope='future' + 분할 지점 뒤에 예외 행 존재 → 409 RECURRENCE_FUTURE_HAS_EXCEPTIONS
 */
export async function patchEventScoped(
  id: string,
  input: PatchEventInput,
  opts?: EventScopeOpts,
  sink?: ExternalInviteSink,
): Promise<MoveEventResult> {
  const params = scopeParams(opts);
  if (!params) {
    await patchEvent(id, input, sink);
    return { scope: 'all', newMasterId: null, movedWholeSeries: false };
  }
  const res = await apiClient.patch<{ scope?: EventScope; newMasterId?: string | null; movedWholeSeries?: boolean }>(
    `/calendar/events/${id}`, input, { params },
  );
  sink?.(readExternalEnvelope(res.data));
  return {
    scope: res.data?.scope ?? (opts?.scope ?? 'all'),
    newMasterId: res.data?.newMasterId ?? null,
    movedWholeSeries: res.data?.movedWholeSeries === true,
  };
}

/** 시간 이동(reschedule) — patchEventScoped 의 시각 전용 표면. 드래그 이동이 쓴다. */
export async function moveEvent(
  id: string,
  times: { startAt: string; endAt: string },
  opts?: EventScopeOpts,
): Promise<MoveEventResult> {
  return patchEventScoped(id, times, opts);
}

export interface ReminderDto {
  id: string;
  eventId: string;
  userId: string;
  method: ReminderMethod;
  minutesBefore: number;
  createdAt: string;
}

export interface CreateReminderInput {
  method: ReminderMethod;
  minutesBefore: number;
}

export async function listReminders(eventId: string): Promise<ReminderDto[]> {
  const res = await apiClient.get<{ reminders: ReminderDto[] }>(`/calendar/events/${eventId}/reminders`);
  return Array.isArray(res.data?.reminders) ? res.data.reminders : [];
}

export async function createReminder(eventId: string, input: CreateReminderInput): Promise<ReminderDto> {
  const res = await apiClient.post<{ reminder: ReminderDto }>(`/calendar/events/${eventId}/reminders`, input);
  return res.data.reminder;
}

export async function deleteReminder(eventId: string, reminderId: string): Promise<void> {
  await apiClient.delete(`/calendar/events/${eventId}/reminders/${reminderId}`);
}

// ── 공휴일 (전역 읽기) ──
export interface HolidayDto {
  id: string;
  date: string; // YYYY-MM-DD
  name: string;
  countryCode: string;
  isSubstitute: boolean;
  year: number;
}

/**
 * `/holidays/effective` 가 «없는 길»(404)이었나 — 한 번 보면 세션 동안 기억해 그 뒤로는 바로
 * `/holidays` 만 부른다(2026-10-30). 옛 서버에서 매번 요청이 두 배가 되던 것을 막는다.
 * ⚠ 로그아웃하면 잊는다 — 다른 사람이 로그인할 때쯤엔 서버가 바뀌었을 수 있다.
 */
let holidaysEffectiveMissing = false;
registerSessionCache(() => { holidaysEffectiveMissing = false; });

/** 시험 전용 — 「없는 길」 기억을 지운다. */
export function _resetHolidaysRouteMemoForTests(): void {
  holidaysEffectiveMissing = false;
}

/**
 * 캘린더가 그리는 공휴일.
 *
 * **2026-08-24 (근태 4단계)** — 먼저 **합친 결과**(`/holidays/effective`)를 부른다. 회사가 정한
 * 날(창립기념일)과 안 쉬기로 한 날이 캘린더에도 그대로 보여야 하기 때문이다 — 두 곳에서 각자
 * 합치면 같은 날이 화면마다 달라진다.
 *
 * ★ 옛 서버에는 그 길이 없다(404). 그때는 **전역 목록으로 물러난다** — 프론트를 먼저 배포해도
 *   캘린더가 비지 않게 하려는 것이다(메일 정렬에서 쓴 것과 같은 수법: 배포 순서 결합 제거).
 * ⚠ 2026-10-30 — 물러서는 것은 «404 일 때만»이다. 다른 실패(429·5xx·네트워크)는 그대로 던진다.
 */
export async function listHolidays(year: number, country = 'KR'): Promise<HolidayDto[]> {
  if (!holidaysEffectiveMissing) {
    try {
      const eff = await listEffectiveHolidays(year, country);
      if (eff) return eff;
    } catch (e) {
      // ⭐ 2026-10-30 — «404 일 때만» 전역 목록으로 물러난다. 429·5xx·네트워크 오류에서 물러서면
      //   요청을 하나 더 보태 폭주를 키운다(6101 실측: effective·holidays 가 줄줄이 429) ⇒ 그대로 실패.
      if (!(axios.isAxiosError(e) && e.response?.status === 404)) throw e;
      holidaysEffectiveMissing = true;
    }
  }
  const res = await apiClient.get<{ holidays: HolidayDto[] }>('/holidays', { params: { year, country } });
  // 방어: 응답에 holidays 필드가 없거나 배열이 아니어도 [] 반환(undefined resolve 차단 → MonthView 안전).
  return Array.isArray(res.data?.holidays) ? res.data.holidays : [];
}

/** `/holidays/effective` 한 번 — 응답 모양이 어긋나면 null(종전대로 전역 목록으로 간다). */
async function listEffectiveHolidays(year: number, country: string): Promise<HolidayDto[] | null> {
  const eff = await apiClient.get<{ holidays: Array<{ date: string; name: string; isSubstitute: boolean }> }>(
    '/holidays/effective', { params: { year, country } },
  );
  if (!Array.isArray(eff.data?.holidays)) return null;
  return eff.data.holidays.map((h, i) => ({
    id: `eff-${h.date}-${i}`, date: h.date, name: h.name,
    countryCode: country, isSubstitute: h.isSubstitute, year,
  }));
}
